import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import type { SessionUser } from '../api';
import {
  ArchiveButton,
  ArchivedToggle,
  Button,
  Card,
  Empty,
  ErrorBanner,
  Field,
  FormGrid,
  List,
  ListRow,
  Modal,
  ModalActions,
  PageHead,
  StatusPill,
  useAction,
  useLoad,
} from '../ui';

/**
 * The top of the tree, and staff-only: a customer login has exactly one organization
 * and is routed straight into it, so this list would be a page of one.
 */
export function Organizations({ user }: { user: SessionUser }) {
  const isStaff = user.role === 'staff';
  const [showArchived, setShowArchived] = useState(false);
  const orgs = useLoad(() => api.organizations.list(showArchived), [showArchived]);
  const action = useAction();

  const [creating, setCreating] = useState(false);

  return (
    <>
      <PageHead
        title="Organizations"
        hint="One per customer. Outlets, counters, users and payment providers all hang off these."
        actions={
          <>
            <ArchivedToggle showArchived={showArchived} onChange={setShowArchived} />
            {isStaff && <Button onClick={() => setCreating(true)}>New organization</Button>}
          </>
        }
      />

      <ErrorBanner message={orgs.error ?? action.error} />

      <Modal open={creating} title="Onboard a customer" onClose={() => setCreating(false)}>
        <NewOrganization
          onCreated={() => {
            setCreating(false);
            orgs.reload();
          }}
        />
      </Modal>

      {orgs.loading && <Empty>Loading…</Empty>}
      {!orgs.loading && orgs.data?.length === 0 && (
        <Card>
          <Empty>
            {showArchived ? 'Nothing here, archived or otherwise.' : 'No organizations yet.'}
          </Empty>
        </Card>
      )}

      {orgs.data && orgs.data.length > 0 && (
        <List>
          {orgs.data.map((org) => (
            <ListRow
              key={org.id}
              archived={org.status === 'archived'}
              title={
                <>
                  <Link to={`/organizations/${org.id}/outlets`} className="text-accent">
                    {org.name}
                  </Link>
                  <StatusPill status={org.status} />
                </>
              }
              subtitle={
                <>
                  <span className="font-mono text-xs">{org.slug}</span>
                  {org._count && (
                    <>
                      {' · '}
                      {org._count.outlets} outlet{org._count.outlets === 1 ? '' : 's'}
                      {' · '}
                      {org._count.users} user{org._count.users === 1 ? '' : 's'}
                    </>
                  )}
                </>
              }
              actions={
                isStaff && (
                  <ArchiveButton
                    status={org.status}
                    label={org.name}
                    cascadeNote="Its outlets, counters and payment providers are archived with it, and its counters stop taking payments."
                    busy={action.busy}
                    onArchive={() =>
                      void action.run(() => api.organizations.archive(org.id)).then(orgs.reload)
                    }
                    onRestore={() =>
                      void action.run(() => api.organizations.restore(org.id)).then(orgs.reload)
                    }
                  />
                )
              }
            />
          ))}
        </List>
      )}
    </>
  );
}

function NewOrganization({ onCreated }: { onCreated: () => void }) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const action = useAction();

  async function create(event: FormEvent) {
    event.preventDefault();
    const ok = await action.run(() => api.organizations.create(name.trim(), slug.trim()));
    if (ok) {
      setName('');
      setSlug('');
      onCreated();
    }
  }

  return (
    <form onSubmit={create} className="flex flex-col gap-3">
      <ErrorBanner message={action.error} />
      <FormGrid>
        <Field label="Name">
          <input
            required
            autoFocus
            value={name}
            placeholder="Ihsaan Fihaara"
            onChange={(event) => {
              setName(event.target.value);
              // A slug nobody typed is one nobody has to think about; it stays
              // editable because it ends up in URLs and cannot change later.
              setSlug(
                event.target.value
                  .toLowerCase()
                  .replace(/[^a-z0-9]+/g, '-')
                  .replace(/^-|-$/g, ''),
              );
            }}
          />
        </Field>
        <Field label="Slug" hint="Lowercase letters, digits and hyphens.">
          <input
            required
            value={slug}
            placeholder="ihsaan-fihaara"
            onChange={(event) => setSlug(event.target.value)}
          />
        </Field>
      </FormGrid>
      <ModalActions>
        <Button type="submit" disabled={action.busy}>
          {action.busy ? 'Creating…' : 'Create organization'}
        </Button>
      </ModalActions>
    </form>
  );
}
